import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { LANGUAGES } from "@/i18n";

export default function LanguageSwitcher({ variant = "default", testid = "lang-switcher" }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = LANGUAGES.find((l) => l.code === (i18n.language || "en").split("-")[0]) || LANGUAGES[0];

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const trigger =
    variant === "compact"
      ? "flex items-center gap-1.5 px-3 min-h-[44px] rounded-full text-xs font-bold uppercase tracking-wider border border-[rgba(6,78,59,0.2)] bg-white text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"
      : "flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border border-[rgba(6,78,59,0.2)] bg-white text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)] min-h-[44px]";

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("common.select_language")}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testid}-trigger`}
        className={trigger}
      >
        <Globe className="w-4 h-4" strokeWidth={2.2} />
        <span>{current.short}</span>
      </button>

      {open && (
        <div
          role="listbox"
          data-testid={`${testid}-menu`}
          className="absolute right-0 mt-2 w-52 rounded-2xl border border-[rgba(6,78,59,0.12)] bg-white shadow-2xl p-2 z-50"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--tr-muted)] font-bold px-3 py-2">
            {t("common.select_language")}
          </div>
          {LANGUAGES.map((lng) => (
            <button
              key={lng.code}
              type="button"
              role="option"
              aria-selected={current.code === lng.code}
              data-testid={`${testid}-option-${lng.code}`}
              onClick={() => choose(lng.code)}
              className={`w-full flex items-center justify-between px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
                current.code === lng.code
                  ? "bg-[var(--tr-deep-green)] text-white"
                  : "text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"
              }`}
            >
              <span>{lng.label}</span>
              {current.code === lng.code && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
